import { memo } from 'react'
import AccountUsageIndicator from '../../components/AccountUsageIndicator'
import MediaThumbnail from '../../components/MediaThumbnail'
import MediaTypeLabel from '../../components/MediaTypeLabel'
import MediaLibraryButton from '../../components/MediaLibraryButton'
import Tooltip from '../../components/Tooltip'
import { useI18n } from '../../i18n'
import { getMediaPreview, shouldUseFullQualityThumbnail } from './utils'

export default memo(function ModelDetailMediaItem({
  item,
  sessionId,
  index,
  modelId,
  isSelected,
  onToggle,
  onView,
  onEdit,
  canReorder = false,
  isDragging = false,
  isSourceHidden = false,
  highQualityThumbnail = false,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onRemoveFromSession,
  onLibrarySaved,
}) {
  const { t } = useI18n()
  const fullQualityThumb = highQualityThumbnail && shouldUseFullQualityThumbnail(item)
  const preview = getMediaPreview(item, { preferFull: fullQualityThumb })
  const itemId = item.id ?? item.external_id ?? index
  const counters = item.counters ?? {}
  const likes = counters?.likesCount ?? 0
  const statusLabel = item.preview_status
  const isPaid = !!item.is_paid
  const paidLabel = isPaid ? t('media.paid') : t('media.free')
  const mediaPriceLabel = item.price_min != null || item.price_max != null
    ? `$${item.price_min ?? item.price_max ?? 0}${(item.price_max ?? item.price_min) !== (item.price_min ?? item.price_max) ? `-${item.price_max ?? item.price_min}` : ''}`
    : ''
  const excludedFromAutoUse = !!item.excluded_from_auto_use
  const isHidden = !!item.is_hidden
  const categoryKey = item.content_category || ''
  const categoryTranslationKey = categoryKey ? `media.category.${categoryKey}` : ''
  const translatedCategoryLabel = categoryTranslationKey ? t(categoryTranslationKey) : ''
  const categoryLabel = translatedCategoryLabel === categoryTranslationKey
    ? categoryKey.replace(/[_-]+/g, ' ').replace(/^./, (char) => char.toUpperCase())
    : translatedCategoryLabel
  const hasDescription =
    item.description != null && String(item.description || '').trim() !== ''
  const hasHotness =
    item.hotness != null && item.hotness >= 1 && item.hotness <= 10
  const hasBodyParts = Array.isArray(item.body_parts) && item.body_parts.length > 0
  const canRemoveFromSession = !!sessionId && !!item?.id && !!onRemoveFromSession

  const isReadyForUse = !!item.is_ready_for_use && !excludedFromAutoUse
  const missingKeys = []
  if (!hasDescription) missingKeys.push('description')
  if (!hasHotness) missingKeys.push('hotness')
  if (!hasBodyParts) missingKeys.push('bodyParts')

  const usageTitle = excludedFromAutoUse
    ? t('media.used.excluded')
    : isReadyForUse
      ? t('media.used.ok')
      : missingKeys.length > 0
      ? [
          t('media.used.notActive'),
          t('media.used.fillPrompt'),
          ...missingKeys.map((k) => ` - ${t(`media.used.missing.${k}`)}`),
        ].join('\n')
      : t('media.used.bad')

  function handleDragStart(e) {
    if (!canReorder) return
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(index))
    onDragStart?.(sessionId, index)
  }

  function handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
    onDragOver?.(e, sessionId, index)
  }

  function handleDrop(e) {
    e.preventDefault()
    onDrop?.(sessionId)
  }

  const cls = [
    'model-detail-media-item',
    'model-detail-media-item--selectable',
    isSelected && 'model-detail-media-item--selected',
    excludedFromAutoUse && 'model-detail-media-item--excluded',
    isHidden && 'model-detail-media-item--hidden',
    isDragging && 'model-detail-media-item--dragging',
    isSourceHidden && 'model-detail-media-item--source-hidden',
  ].filter(Boolean).join(' ')

  const handleThumbClick = () => {
    onView?.(item)
  }

  const handleCheckboxClick = (e) => {
    e.stopPropagation()
    onToggle?.(item.id)
  }

  return (
    <div
      key={itemId}
      className={cls}
      draggable={canReorder ? 'true' : undefined}
      onDragStart={canReorder ? handleDragStart : undefined}
      onDragOver={canReorder ? handleDragOver : undefined}
      onDrop={canReorder ? handleDrop : undefined}
      onDragEnd={canReorder ? onDragEnd : undefined}
      onClick={handleThumbClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget || !['Enter', ' '].includes(e.key)) return
        e.preventDefault()
        handleThumbClick()
      }}
      aria-label={t('media.viewer.aria.view')}
    >
      <div className="model-detail-media-thumb-wrap">
        <button
          type="button"
          className="media-edit-pencil-badge"
          onClick={(e) => {
            e.stopPropagation()
            onEdit(item)
          }}
          aria-label={t('media.edit.modal.title')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
        {canRemoveFromSession ? (
          <button
            type="button"
            className="media-remove-session-badge"
            onClick={(e) => {
              e.stopPropagation()
              onRemoveFromSession(sessionId, item.id)
            }}
            aria-label={t('media.library.removeFromScript')}
            title={t('media.library.removeFromScriptHint')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        ) : null}
        <AccountUsageIndicator
          isReady={isReadyForUse}
          title={usageTitle}
          className="media-usage-indicator"
        />
        {preview ? (
          preview.local ? (
            <MediaThumbnail
              modelId={modelId}
              mediaId={preview.mediaId}
              kind={fullQualityThumb ? 'full' : 'thumb'}
              fallbackKind="preview"
              className="model-detail-media-thumb"
            />
          ) : (
            <MediaThumbnail modelId={modelId} cdnUrl={preview.cdnUrl} className="model-detail-media-thumb" />
          )
        ) : (
          <div className="model-detail-media-placeholder">
            {statusLabel === 'downloading' ? (
              <span className="media-status-loading" aria-label={t('media.loading')} />
            ) : statusLabel === 'pending' ? (
              t('media.queue')
            ) : statusLabel === 'failed' ? (
              t('media.error')
            ) : (
              t('media.noPreview')
            )}
          </div>
        )}
        {excludedFromAutoUse && (
          <span className="media-excluded-badge">{t('media.excluded.badge')}</span>
        )}
        {isHidden && (
          <span className="media-hidden-badge" title={item.provider_missing_since ? 'Removed at provider' : 'Hidden in FanLTV'}>
            {item.provider_missing_since ? 'Provider removed' : 'Hidden'}
          </span>
        )}
        <Tooltip text={t('media.select.hint')}>
          <button
            type="button"
            className={`model-detail-media-checkbox${isSelected ? ' model-detail-media-checkbox--checked' : ''}`}
            onClick={handleCheckboxClick}
            aria-label={isSelected ? t('media.selection.clear') : t('media.select.hint')}
            aria-pressed={isSelected}
          >
            {isSelected ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <span className="model-detail-media-checkbox-empty" aria-hidden />
            )}
          </button>
        </Tooltip>
      </div>
      <MediaTypeLabel item={item} />
      {(likes > 0 || paidLabel || categoryLabel || mediaPriceLabel) && (
        <p className="model-detail-media-caption">
          <span className="model-detail-media-caption-left">
            {[categoryLabel || paidLabel, mediaPriceLabel].filter(Boolean).join(' · ')}
          </span>
          {likes > 0 && (
            <span className="media-likes model-detail-media-caption-right">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              {likes}
            </span>
          )}
        </p>
      )}
      <MediaLibraryButton modelId={modelId} item={item} onSaved={onLibrarySaved} />
    </div>
  )
})
